<template>
    <div class="overlayPalette">
        <div class="op-rail">
            <!-- Shortcut straight to the crop tool, duplicated here from the "Vidéo"/"Image"
                 flyout below - quick access without opening that category first. -->
            <Tooltip
                :text="$t('dash.post-creator.media-editor.palette.tool-ratio')"
                position="right"
                :classCustom="'op-rail-item' + (activeTool === 'crop' ? ' active' : '')"
                @click="selectRatio"
            >
                <i class="fa-solid fa-crop"></i>
                <span class="op-rail-label">{{ $t('dash.post-creator.media-editor.palette.tool-ratio') }}</span>
            </Tooltip>

            <div class="op-rail-separator"></div>

            <Tooltip
                :text="$t(photoMode ? 'dash.post-creator.media-editor.palette.section-video-photo' : 'dash.post-creator.media-editor.palette.section-video')"
                position="right"
                :classCustom="'op-rail-item op-rail-item-video' + (activeCategory === 'video' ? ' active' : '')"
                @click="selectCategory('video')"
            >
                <i :class="photoMode ? 'fa-solid fa-image' : 'fa-solid fa-clapperboard'"></i>
                <span class="op-rail-label">{{ $t(photoMode ? 'dash.post-creator.media-editor.palette.section-video-photo' : 'dash.post-creator.media-editor.palette.section-video') }}</span>
            </Tooltip>

            <div class="op-rail-separator"></div>

            <Tooltip
                :text="$t('dash.post-creator.media-editor.palette.text')"
                position="right"
                :classCustom="'op-rail-item' + (activeCategory === 'text' ? ' active' : '')"
                @click="selectCategory('text')"
            >
                <i class="fa-solid fa-font"></i>
                <span class="op-rail-label">{{ $t('dash.post-creator.media-editor.palette.text') }}</span>
            </Tooltip>

            <Tooltip
                :text="$t('dash.post-creator.media-editor.palette.section-elements')"
                position="right"
                :classCustom="'op-rail-item' + (activeCategory === 'elements' ? ' active' : '')"
                @click="selectCategory('elements')"
            >
                <i class="fa-solid fa-shapes"></i>
                <span class="op-rail-label">{{ $t('dash.post-creator.media-editor.palette.section-elements') }}</span>
            </Tooltip>

            <Tooltip
                :text="$t('dash.post-creator.media-editor.palette.section-widgets')"
                position="right"
                :classCustom="'op-rail-item' + (activeCategory === 'widgets' ? ' active' : '')"
                @click="selectCategory('widgets')"
            >
                <i class="fa-solid fa-icons"></i>
                <span class="op-rail-label">{{ $t('dash.post-creator.media-editor.palette.section-widgets') }}</span>
            </Tooltip>

            <Tooltip
                v-if="!photoMode"
                :text="$t('dash.post-creator.media-editor.palette.tool-music')"
                position="right"
                :classCustom="'op-rail-item' + (activeTool === 'music' ? ' active' : '')"
                @click="selectMusic"
            >
                <i class="fa-solid fa-music"></i>
                <span class="op-rail-label">{{ $t('dash.post-creator.media-editor.palette.tool-music') }}</span>
            </Tooltip>
        </div>

        <!-- Video group is first on the rail and gets its own accent color (see .op-rail-item-video/
             .ot-item-video below) - these act on the clip itself (crop/rotate/split/...), not
             "add an overlay on top of it" like every other group here, so they read as a visually
             distinct family rather than another widget bucket. Crop first, matching what used to
             be the first tool before these moved out to the timeline (see Timeline.vue's clipTools -
             kept there too for now, not removed). -->
        <div class="op-flyout" v-if="activeCategory === 'video'">
            <div class="op-list">
                <div class="ot-item ot-item-video" v-for="tool in visibleVideoToolOptions" :key="tool.type" @click="onVideoToolClick(tool)">
                    <div class="ot-item-icon"><i :class="tool.icon"></i></div>
                    <span>{{ $t(tool.labelKey) }}</span>
                </div>
            </div>
        </div>

        <!-- Preset text starting points - see textPresetOptions. -->
        <div class="op-flyout" v-if="activeCategory === 'text'">
            <div class="op-list">
                <div
                    class="ot-item ot-item-text"
                    v-for="preset in visibleTextPresetOptions"
                    :key="preset.key"
                    @click="startPlacement('text', null, preset.dataOverride, preset.transformOverride, preset.animationOverride)"
                >
                    <div class="ot-item-icon"><i :class="preset.icon"></i></div>
                    <span>{{ $t(preset.labelKey) }}</span>
                </div>
            </div>
        </div>

        <div class="op-flyout" v-if="activeCategory === 'elements'">
            <div class="op-list">
                <div class="ot-item" v-for="shape in shapeOptions" :key="shape.type" @click="startPlacement('shape', shape.type)">
                    <div class="ot-item-icon" :style="{ backgroundColor: shape.color }"><i :class="shape.icon"></i></div>
                    <span>{{ $t(shape.labelKey) }}</span>
                </div>
            </div>
        </div>

        <div class="op-flyout" v-if="activeCategory === 'widgets'">
            <div class="op-list">
                <input ref="imageInput" type="file" accept="image/*" hidden @change="onImageFilePicked">
                <div class="ot-item" @click="$refs.imageInput.click()">
                    <div class="ot-item-icon" :style="{ backgroundColor: '#5c5c5c' }"><i class="fa-solid fa-image"></i></div>
                    <span>{{ $t('dash.post-creator.media-editor.palette.image') }}</span>
                </div>
                <div class="ot-item" v-for="widget in visibleWidgetOptions" :key="widget.type" @click="onWidgetClick(widget)">
                    <div class="ot-item-icon" :style="{ backgroundColor: widget.color }"><i :class="widget.icon"></i></div>
                    <span>{{ $t(widget.labelKey) }}</span>
                </div>
            </div>
        </div>

        <!-- The gif itself has to be picked before placement can start (see onGifSelected) - rather
             than placing an empty gif widget first and only choosing its content afterward, which
             left a blank component on the timeline for good the moment a search came up empty/was
             abandoned. Floats as its own wider panel (not squeezed into the narrow rail+flyout
             column, see .op-gif-flyout) - a 3-column thumbnail grid needs real room to actually
             recognize a gif by, which the ~170px flyout column never had. -->
        <div class="op-gif-flyout" v-if="showGifPicker" :style="gifFlyoutStyle">
            <div class="op-gif-header">
                <i class="fa-solid fa-arrow-left" @click="showGifPicker = false"></i>
                <p class="ot-title">{{ $t('dash.post-creator.media-editor.palette.widget-gif') }}</p>
            </div>
            <GifPicker @select="onGifSelected" />
        </div>
    </div>
</template>

<script>
import { WIDGET_ACCENT_COLORS } from '../engine/constants.js'
import { fetchCurrentWeather } from '../engine/widgets/weatherApi.js'
import Tooltip from '@/components/dash/tooltip/tooltipInit.vue'
import GifPicker from './GifPicker.vue'

export default {
    name: 'OverlayPalette',

    components: {
        Tooltip,
        GifPicker
    },

    props: {
        currentMs: {
            type: Number,
            default: 0
        },
        timelineDurationMs: {
            type: Number,
            default: 5000
        },
        activeTool: {
            type: String,
            default: null
        },
        photoMode: {
            type: Boolean,
            default: false
        }
    },

    data() {
        return {
            // No rail category open by default - MediaEditor.vue opens the "Ratio" shortcut
            // itself on mount (activeClipTool = 'crop'), and that shortcut lives at the top level,
            // not inside this "Vidéo"/"Image" flyout - both open at once looked redundant.
            activeCategory: null,
            showGifPicker: false,
            gifFlyoutStyle: {},
            videoToolOptions: [
                { type: 'crop', icon: 'fa-solid fa-crop', labelKey: 'dash.post-creator.media-editor.palette.tool-crop' },
                { type: 'rotate', icon: 'fa-solid fa-rotate', labelKey: 'dash.post-creator.media-editor.palette.tool-rotate' },
                { type: 'split', action: 'split', icon: 'fa-solid fa-scissors', labelKey: 'dash.post-creator.media-editor.palette.tool-split' },
                { type: 'silence', icon: 'fa-solid fa-wand-magic-sparkles', labelKey: 'dash.post-creator.media-editor.clip-tool.remove-silences' },
                { type: 'speed', icon: 'fa-solid fa-gauge-high', labelKey: 'dash.post-creator.media-editor.palette.tool-speed' },
                { type: 'background', icon: 'fa-solid fa-fill-drip', labelKey: 'dash.post-creator.media-editor.palette.tool-background' },
                { type: 'sound', icon: 'fa-solid fa-volume-high', labelKey: 'dash.post-creator.media-editor.palette.tool-sound' }
            ],
            shapeOptions: [
                { type: 'rectangle', icon: 'fa-regular fa-square', labelKey: 'dash.post-creator.media-editor.palette.shape-rectangle', color: '#5c5c5c' },
                { type: 'circle', icon: 'fa-regular fa-circle', labelKey: 'dash.post-creator.media-editor.palette.shape-circle', color: '#5c5c5c' },
                { type: 'oval', icon: 'fa-regular fa-circle', labelKey: 'dash.post-creator.media-editor.palette.shape-oval', color: '#5c5c5c' },
                { type: 'triangle', icon: 'fa-solid fa-caret-up', labelKey: 'dash.post-creator.media-editor.palette.shape-triangle', color: '#5c5c5c' },
                { type: 'line', icon: 'fa-solid fa-minus', labelKey: 'dash.post-creator.media-editor.palette.shape-line', color: '#5c5c5c' }
            ],
            widgetOptions: [
                { type: 'place', icon: 'fa-solid fa-location-dot', labelKey: 'dash.post-creator.media-editor.palette.widget-place', color: WIDGET_ACCENT_COLORS.place },
                { type: 'datetime', icon: 'fa-solid fa-clock', labelKey: 'dash.post-creator.media-editor.palette.widget-datetime', color: WIDGET_ACCENT_COLORS.datetime },
                { type: 'weather', icon: 'fa-solid fa-sun', labelKey: 'dash.post-creator.media-editor.palette.widget-weather', color: WIDGET_ACCENT_COLORS.weather },
                { type: 'gif', icon: 'fa-solid fa-film', labelKey: 'dash.post-creator.media-editor.palette.widget-gif', color: WIDGET_ACCENT_COLORS.gif },
                { type: 'music', icon: 'fa-solid fa-music', labelKey: 'dash.post-creator.media-editor.palette.widget-music', color: WIDGET_ACCENT_COLORS.music },
                { type: 'hashtag', icon: 'fa-solid fa-hashtag', labelKey: 'dash.post-creator.media-editor.palette.widget-hashtag', color: WIDGET_ACCENT_COLORS.hashtag },
                { type: 'mention', icon: 'fa-solid fa-at', labelKey: 'dash.post-creator.media-editor.palette.widget-mention', color: WIDGET_ACCENT_COLORS.mention }
            ],
            // All three are type: 'text' - just different data/transform/animation starting points.
            textPresetOptions: [
                { key: 'plain', icon: 'fa-solid fa-font', labelKey: 'dash.post-creator.media-editor.palette.text-preset-plain', dataOverride: {}, transformOverride: {}, animationOverride: {} },
                {
                    key: 'title',
                    icon: 'fa-solid fa-heading',
                    labelKey: 'dash.post-creator.media-editor.palette.text-preset-title',
                    dataOverride: { text: 'Titre', fontSize: 72, bold: true },
                    transformOverride: {},
                    animationOverride: {}
                },
                {
                    key: 'animated',
                    icon: 'fa-solid fa-wand-magic-sparkles',
                    labelKey: 'dash.post-creator.media-editor.palette.text-preset-animated',
                    dataOverride: { text: 'Votre texte', fontSize: 54, color: '#FFFFFF', backgroundColor: '#b64985' },
                    transformOverride: {},
                    animationOverride: {
                        animationIn: { styles: ['slide', 'blur'], direction: 'left', durationMs: 600 },
                        animationLinkMode: 'mirror'
                    }
                }
            ]
        }
    },

    watch: {
        // .overlayPalette clips its own content (overflow: hidden - the rail+flyout shell must
        // never spill into the canvas), so the floating gif picker (see .op-gif-flyout) is
        // position: fixed instead, computed from the palette's live screen rect rather than
        // relying on any CSS containing block that overflow would clip it against.
        showGifPicker(isOpen) {
            if (isOpen) {
                this.updateGifFlyoutPosition()
                window.addEventListener('resize', this.updateGifFlyoutPosition)
            } else {
                window.removeEventListener('resize', this.updateGifFlyoutPosition)
            }
        }
    },

    beforeDestroy() {
        window.removeEventListener('resize', this.updateGifFlyoutPosition)
    },

    computed: {
        // A still image has no timeline to split/slow down/remove silence from, and no audio of
        // its own to mute - only crop/rotate/background still apply.
        visibleVideoToolOptions() {
            if (!this.photoMode) return this.videoToolOptions
            return this.videoToolOptions.filter(tool => ['crop', 'rotate', 'background'].includes(tool.type))
        },

        // Animated gif stickers make no sense once export is a single static frame.
        visibleWidgetOptions() {
            if (!this.photoMode) return this.widgetOptions
            return this.widgetOptions.filter(widget => widget.type !== 'gif')
        },

        // The "animated" preset bakes in an entrance/exit animation, which the Animation tab
        // itself is already hidden for in photo mode - offering it here would be a dead end.
        visibleTextPresetOptions() {
            if (!this.photoMode) return this.textPresetOptions
            return this.textPresetOptions.filter(preset => preset.key !== 'animated')
        }
    },

    methods: {
        // Same mutual-exclusivity as selectCategory/selectMusic (closes an already-open rail
        // category flyout) - and also self-toggles closed, since unlike a category this shortcut
        // has no separate "flyout" state of its own, only the ClipToolPanel it opens directly.
        selectRatio() {
            this.activeCategory = null
            this.showGifPicker = false
            if (this.activeTool === 'crop') this.$emit('close-side-panel')
            else this.$emit('select-clip-tool', 'crop')
        },

        // Opening a rail category (Vidéo/Éléments/Widgets) is a different "mode" than whatever
        // side panel (MusicPanel, or a Vidéo tool's own ClipToolPanel) happens to already be open
        // from a previous group - without closing it, you could end up with e.g. the Widgets flyout
        // open on this side AND the music config panel open on the other, showing two unrelated
        // things at once. close-side-panel tells MediaEditor.vue to clear activeClipTool; harmless
        // if nothing was open.
        selectCategory(key) {
            this.activeCategory = this.activeCategory === key ? null : key
            this.showGifPicker = false
            this.$emit('close-side-panel')
        },

        // Same mutual-exclusivity as selectCategory, other direction - opening the music panel
        // (a side-panel "mode" of its own) closes whichever rail category flyout was open.
        selectMusic() {
            this.activeCategory = null
            this.showGifPicker = false
            this.$emit('select-clip-tool', 'music')
        },

        updateGifFlyoutPosition() {
            const rect = this.$el.getBoundingClientRect()
            const margin = 10
            this.gifFlyoutStyle = {
                left: `${rect.left + margin}px`,
                top: `${rect.top}px`,
                maxHeight: `${window.innerHeight - rect.top - margin}px`
            }
        },

        // Mirrors Timeline.vue's onClipToolClick (same events, same fallback-to-clip-under-playhead
        // behavior on the MediaEditor.vue side - see onSelectClipTool/splitSelectedClip there) since
        // these tools are now reachable from both places.
        onVideoToolClick(tool) {
            if (tool.action === 'split') this.$emit('split-clip')
            else this.$emit('select-clip-tool', tool.type)
        },

        onWidgetClick(widget) {
            if (widget.type === 'weather') this.startWeatherPlacement()
            else if (widget.type === 'gif') this.showGifPicker = true
            else this.startPlacement('widget', widget.type)
        },

        // Placement (choosing where it goes on the video) only starts once a real gif has actually
        // been picked - see the gif-picker template block above.
        onGifSelected(gifUrl) {
            this.showGifPicker = false
            this.startPlacement('widget', 'gif', { gifUrl })
        },

        defaultWindow() {
            const startMs = this.currentMs
            const endMs = Math.min(this.timelineDurationMs, startMs + 3000)
            return { startMs, endMs }
        },

        // Emits the overlay config up to MediaEditor without a position yet - the next click on
        // the video canvas will supply xPct/yPct and actually create it (see MediaEditor.onPlaceOverlay).
        // dataOverride: lets startWeatherPlacement inject real fetched conditions over the defaults.
        // transformOverride/animationOverride: same idea for the text presets (see textPresetOptions).
        startPlacement(type, subType, dataOverride = {}, transformOverride = {}, animationOverride = {}) {
            const { startMs, endMs } = this.defaultWindow()
            const dataDefaults = {
                text: { text: 'Votre texte', fontFamily: 'Poppins', fontSize: 42, color: '#FFFFFF', align: 'center', bold: false, italic: false, underline: false, strikethrough: false },
                shape: { shape: subType, color: 'rgba(255,255,255,0.6)' },
                place: { location: 'Paris, France' },
                datetime: { format: 'datetime' },
                weather: { temperature: 21, unit: 'celsius', emoji: '☀️' },
                gif: { gifUrl: '' },
                music: { title: 'Titre - Artiste' },
                hashtag: { tag: 'monhashtag' },
                mention: { username: 'monpseudo' }
            }

            const config = {
                type,
                widgetType: type === 'widget' ? subType : null,
                startMs,
                endMs,
                transform: { ...(type === 'widget' ? { widthPct: 0.4, heightPct: 0.08 } : (type === 'shape' ? { widthPct: 0.3, heightPct: subType === 'circle' ? 0.15 : 0.15 } : {})), ...transformOverride },
                data: { ...(dataDefaults[type === 'widget' ? subType : type] || {}), ...dataOverride },
                ...animationOverride
            }

            this.$emit('start-placement', config)
        },

        // Best-effort real conditions (Open-Meteo, via the browser's own geolocation) prefill the
        // widget instead of the static mock - falls back to the plain defaults (startPlacement's
        // own dataOverride={}) on any failure/denied permission/timeout, so placing the widget
        // never actually waits on or breaks because of this.
        async startWeatherPlacement() {
            const weather = await fetchCurrentWeather()
            this.startPlacement('widget', 'weather', weather || {})
        },

        onImageFilePicked(e) {
            const file = e.target.files[0]
            e.target.value = null
            if (!file) return
            this.$emit('start-image-placement', file)
        }
    }
}
</script>

<style lang="scss" scoped></style>
